import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { Center, LoadingDots, spacing, Text } from '@retailer-platform/shared-components'
import { RoleManagementFeatureDetails } from 'domains/role-management/components/feature-details/RoleManagementFeatureDetails'
import { type Role } from 'domains/role-management/components/roles-search-list/utils/useRetailerRoles.hooks'
import {
  type TranslatedFeatureGroup,
  useGroupedPermissionsDictionary,
} from 'domains/role-management/components/role-assigned-permissions/usePermissionsDictionary'
import { useDomainApolloErrorMessage } from 'domains/role-management/utils/domain/apollo'

export const PermissionList = (props: { role: Role }) => {
  const permissions = props.role?.permissions
  const permissionSlugs = useMemo(() => permissions?.map(perm => perm.slug), [permissions])
  const { dictionary, loading, error } = useGroupedPermissionsDictionary(
    props.role?.id,
    permissionSlugs
  )
  const i18nError = useDomainApolloErrorMessage(error)

  if (loading) {
    return (
      <Center fillParent>
        <LoadingDots />
      </Center>
    )
  }

  if (error) {
    return (
      <div data-testid="graphql-error-container">
        {i18nError && (
          <div>
            <Text>{i18nError}</Text>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {dictionary.map(featureGroup => (
        <FeatureGroup key={featureGroup.groupName} featureGroup={featureGroup}>
          {featureGroup.features.map(feature => (
            <RoleManagementFeatureDetails key={feature.featureName} feature={feature} />
          ))}
        </FeatureGroup>
      ))}
    </>
  )
}

const FeatureGroupContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X16,
})

const FeatureGroup = (props: {
  featureGroup: TranslatedFeatureGroup
  children: React.ReactNode
}) => (
  <FeatureGroupContainer>
    <Text weight="semibold" size="large">
      {props.featureGroup.groupName}
    </Text>
    {props.children}
  </FeatureGroupContainer>
)
